import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Collapse,
  FormLabel,
  IconButton,
  Input,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  CollapsibleColumn,
  CollapsibleRow,
  SearchState,
  SortState
} from "@/components/02-organisms/CollapsibleTable/types.ts";
import { useIntl } from "react-intl";
import common from "@/i18n/definitions/common";

interface Props {
  columns: CollapsibleColumn[];
  rows: CollapsibleRow[];
  sortedBy?: SortState | null;
  onSortChange?: (sort: SortState) => void;
  searchBy?: SearchState;
  onSearchChange?: (search: SearchState) => void;
  openRows?: Set<string>;
  onOpenRowsChange?: (openRows: Set<string>) => void;
}

/**
 * Renders a collapsible table component with expandable rows,
 * optional sorting and searching support.
 *
 * @param columns Table column/header definitions
 * @param rows Table row data with collapsible content
 * @param sortedBy Current sort state
 * @param onSortChange Callback for sort change
 * @param searchBy Current search state
 * @param onSearchChange Callback for search change
 * @param openRows Controlled set of open row ids
 * @param onOpenRowsChange Callback when open rows change
 */
export default function CollapsibleTable({
  columns,
  rows,
  sortedBy,
  onSortChange,
  searchBy,
  onSearchChange,
  openRows: openRowsProp,
  onOpenRowsChange
}: Props) {
  const intl = useIntl();
  const [internalOpenRows, setInternalOpenRows] = useState<Set<string>>(new Set());

  // Käytä ulkoista tilaa jos annettu, muuten sisäistä
  const openRows = openRowsProp ?? internalOpenRows;
  const setOpenRows = (updater: (prev: Set<string>) => Set<string>) => {
    const next = updater(openRows);
    if (onOpenRowsChange) {
      onOpenRowsChange(next);
    } else {
      setInternalOpenRows(next);
    }
  };

  // Paikallinen tila hakukentille – päivittyy välittömästi käyttäjän kirjoittaessa
  const [localSearch, setLocalSearch] = useState<SearchState>(searchBy ?? {});
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Viimeisin arvo, jonka tämä komponentti on lähettänyt vanhemmalle debounce-kutsun kautta.
  // Käytetään erottamaan omasta debounce-kutsusta palautuva searchBy-prop ulkoisesta muutoksesta.
  const lastSentSearchRef = useRef<SearchState>(searchBy ?? {});

  // Synkronoi ulkopuolinen searchBy paikalliseen tilaan (esim. tyhjennyksen yhteydessä).
  // Ohitetaan, jos muutos on peräisin omasta debounce-kutsusta – muuten ylikirjoitettaisiin
  // käyttäjän mahdollisesti jo kirjoittama uusi teksti.
  useEffect(() => {
    const incoming = searchBy ?? {};
    if (incoming === lastSentSearchRef.current) return;
    setLocalSearch(incoming);
    lastSentSearchRef.current = incoming;
  }, [searchBy]);

  const handleSort = (col: CollapsibleColumn) => {
    if (!col.sortKey || !onSortChange) return;
    const newOrder =
      sortedBy?.sortBy === col.sortKey && sortedBy.orderBy === "asc"
        ? "desc"
        : "asc";
    onSortChange({ sortBy: col.sortKey, orderBy: newOrder });
  };

  const handleSearchChange = (col: CollapsibleColumn, value: string) => {
    if (!col.searchKey || !onSearchChange) return;

    // Päivitä paikallinen tila välittömästi → käyttäjä näkee kirjoittamansa tekstin
    const next: SearchState = { ...localSearch };
    if (value) {
      next[col.id] = value;
    } else {
      delete next[col.id];
    }
    setLocalSearch(next);

    // Debounce API-kutsu 400 ms:n viiveellä
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      lastSentSearchRef.current = next;
      onSearchChange(next);
    }, 400);
  };


  return (
    <TableContainer component={Paper}>
      <Table aria-label="collapsible table">
        <TableHead>
          <TableRow>
            <TableCell width={48} />
            {columns.map(col => (
              <TableCell key={col.id} width={col.width} style={{ verticalAlign: "top" }}>
                {col.sortKey && onSortChange ? (
                  <TableSortLabel
                    active={sortedBy?.sortBy === col.sortKey}
                    direction={
                      sortedBy?.sortBy === col.sortKey
                        ? sortedBy.orderBy
                        : "asc"
                    }
                    onClick={() => handleSort(col)}>
                    {col.label}
                  </TableSortLabel>
                ) : (
                  col.label
                )}
                {col.searchKey && onSearchChange && (
                  <div>
                    <FormLabel>
                      <Input
                        id={`filter-${col.id}`}
                        type="search"
                        value={localSearch[col.id] ?? ""}
                        onChange={e => handleSearchChange(col, e.target.value)}
                        placeholder={intl.formatMessage(common.filterRows)}
                        style={{ fontSize: "0.875rem" }}
                      />
                    </FormLabel>
                  </div>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => {
            const open = openRows.has(row.id);
            const collapseId = `row-collapse-${row.id}`;

            return (
              <React.Fragment key={row.id}>
                <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
                  <TableCell>
                    <IconButton
                      size="small"
                      aria-label={open ? "Sulje rivi" : "Avaa rivi"}
                      aria-expanded={open}
                      aria-controls={collapseId}
                      onClick={() =>
                        setOpenRows(prev => {
                          const next = new Set(prev);
                          if (next.has(row.id)) next.delete(row.id);
                          else next.add(row.id);
                          return next;
                        })
                      }>
                      {open ? (
                        <KeyboardArrowUpIcon />
                      ) : (
                        <KeyboardArrowDownIcon />
                      )}
                    </IconButton>
                  </TableCell>
                  {row.cells.map((cell, i) => (
                    <TableCell key={i}>{cell}</TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell colSpan={columns.length + 1} sx={{ py: 0 }}>
                    <Collapse
                      id={collapseId}
                      in={open}
                      timeout="auto"
                      unmountOnExit>
                      <Box sx={{ p: 2 }}>{row.collapseContent}</Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
